import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SHAREABLE_CATEGORIES, CHARGE_CATEGORIES } from '@his/shared';
import { downloadGet, get, post, qs, session } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, dateTime, humanize, todayIso } from '@/lib/utils';
import { DataTable, Dialog, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, DatePicker, Field, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { Page, ShareChangeView } from './accounts-types';
import { shareRateText } from './accounts-api';
import { categoryDefaultPath, shareChangePath, type ShareTarget } from './ShareChangePage';

interface Overview {
  pendingCount: number;
  backfilled: boolean;
  feeSplittingByBranch: Record<string, boolean>;
  editable: boolean;
  wardRulesAvailable: boolean;
}

interface ServiceRow {
  id: string;
  code: string;
  name: string;
  category: string;
  isActive: boolean;
  sharePct: number | null;
  shareMinor: number | null;
  since: string | null;
  doctorOverrides: number;
  nextChange: string | null;
}

interface RateSlot {
  current: ShareChangeView | null;
  scheduled: ShareChangeView[];
}

interface DoctorRateRow {
  doctorId: string;
  doctorName: string;
  designation: string | null;
  isActive: boolean;
  service: RateSlot;
  category: RateSlot;
  pending: ShareChangeView[];
}

interface WardRuleRow {
  id: string;
  name: string;
  isActive: boolean;
  ward: { name: string } | null;
  chargeItem: { code: string; name: string };
  current: ShareChangeView | null;
  scheduled: ShareChangeView[];
  pending: ShareChangeView[];
}

function shareText(slot: RateSlot | null | undefined): string {
  if (!slot?.current) return '—';
  return shareRateText(slot.current, 'no own rate');
}

function scheduledText(slot: RateSlot | null | undefined): string | null {
  const next = slot?.scheduled[0];
  if (!next) return null;
  return `${shareRateText(next, 'no own rate')} from ${dateOnly(next.effectiveFrom)}`;
}

const TABS = ['services', 'doctors', 'ward', 'waiting', 'history'];

export function useShareScreen(tab: string) {
  const navigate = useNavigate();
  const returnTo = `/accounts/doctor-share?tab=${tab}`;
  return {
    change: (target: ShareTarget) => navigate(shareChangePath(target, returnTo)),
    categoryDefault: () => navigate(categoryDefaultPath(returnTo)),
  };
}

export function DoctorSharePage() {
  const { can } = useAuth();
  const manage = can('doctor_share.manage');
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = params.get('tab');
    return wanted && TABS.includes(wanted) ? wanted : 'services';
  });
  const overview = useQuery({ queryKey: ['doctor-share-overview'], queryFn: () => get<Overview>('/accounts/doctor-share/overview') });
  const editable = manage && Boolean(overview.data?.editable);
  const activeBranch = session.branch();
  const screen = useShareScreen(tab);

  const tabs = [
    { id: 'services', label: tr('billing', 'services') },
    { id: 'doctors', label: tr('billing', 'doctors') },
    ...(overview.data?.wardRulesAvailable ? [{ id: 'ward', label: tr('billing', 'ward_charges') }] : []),
    {
      id: 'waiting',
      label: tr('billing', 'waiting_for_approval'),
      badge: overview.data?.pendingCount ? <Badge variant="warning">{overview.data.pendingCount}</Badge> : undefined,
    },
    { id: 'history', label: tr('billing', 'history') },
  ];

  return (
    <>
      <PageHeader
        title={tr('billing', 'doctor_share')}
        description={tr('billing', 'the_part_of_each_services_collected_money_paid')}
        actions={
          tab === 'services' &&
          editable && (
            <Button variant="outline" onClick={() => screen.categoryDefault()}>
              {tr('billing', 'set_default_for_a_whole_category')}
            </Button>
          )
        }
      />
      {overview.data && !overview.data.editable && (
        <Alert variant="warning" className="mb-4">
          {tr('billing', 'doctor_revenue_share_is_not_enabled_for')} {activeBranch ? tr('billing', 'this_branch') : tr('billing', 'your_branches')}{tr('billing', 'shares_already_on_bills_stay_as_billed')}
        </Alert>
      )}
      <Tabs active={tab} onChange={setTab} tabs={tabs} />
      {tab === 'services' && <ServicesTab editable={editable} />}
      {tab === 'doctors' && <DoctorsTab />}
      {tab === 'ward' && <WardTab editable={editable} />}
      {tab === 'waiting' && <ShareWaitingTab manage={manage} />}
      {tab === 'history' && <ShareHistoryTab />}
    </>
  );
}

function ServicesTab({ editable }: { editable: boolean }) {
  const screen = useShareScreen('services');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [all, setAll] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>();
  const [doctorsOf, setDoctorsOf] = useState<ServiceRow | null>(null);
  const services = useQuery({
    queryKey: ['doctor-share-services', search, category, all, cursor],
    queryFn: () =>
      get<Page<ServiceRow>>(`/accounts/doctor-share/services${qs({ q: search || undefined, category: category || undefined, shareable: all ? '0' : '1', cursor, limit: 50 })}`),
  });
  const paging = (
    <>
      {cursor && <Button variant="outline" size="sm" onClick={() => setCursor(undefined)}>{tr('billing', 'first_page')}</Button>}
      {services.data?.nextCursor && <Button variant="outline" size="sm" onClick={() => setCursor(services.data?.nextCursor ?? undefined)}>{tr('billing', 'next_page')}</Button>}
    </>
  );
  return (
    <>
      <SearchFilterBar
        search={{ value: search, onChange: (value) => { setSearch(value); setCursor(undefined); }, placeholder: tr('billing', 'search_services') }}
        filters={
          <>
            <FilterField label={tr('billing', 'kind')}>
              <Select className="w-48" value={category} onChange={(event) => { setCategory(event.target.value); setCursor(undefined); }}>
                <option value="">{tr('billing', 'all_kinds')}</option>
                {(all ? CHARGE_CATEGORIES : SHAREABLE_CATEGORIES).map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField><Checkbox checked={all} onCheckedChange={(value) => { setAll(value); setCursor(undefined); }} label={tr('billing', 'include_lab_radiology_pharmacy_and_bed_charges')} /></FilterField>
          </>
        }
        onReset={
          search || category || all
            ? () => {
                setSearch('');
                setCategory('');
                setAll(false);
                setCursor(undefined);
              }
            : undefined
        }
      />
      <DataTable
        rows={services.data?.data}
        loading={services.isLoading} refreshing={services.isFetching}
        error={services.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_services')}
        footer={cursor || services.data?.nextCursor ? <div className="ms-auto flex gap-2">{paging}</div> : undefined}
        columns={[
          { header: tr('billing', 'code'), width: '120px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
          { header: tr('billing', 'service'), sort: (row) => row.name, cell: (row) => row.name },
          { header: tr('billing', 'kind'), sort: (row) => row.category, cell: (row) => <Badge variant="secondary">{humanize(row.category)}</Badge> },
          { header: tr('billing', 'default_share'), align: 'right', cell: (row) => shareRateText(row, '—') },
          { header: tr('billing', 'doctors_with_own_rate'), align: 'right', sort: (row) => row.doctorOverrides, cell: (row) => row.doctorOverrides },
          { header: tr('billing', 'next_change'), sort: (row) => row.nextChange, cell: (row) => (row.nextChange ? dateOnly(row.nextChange) : '—') },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setDoctorsOf(row)}>
                  {tr('billing', 'doctors')}
                </Button>
                {editable && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => screen.change({ scope: 'service', chargeItemId: row.id, title: tr('billing', 'default_share_x', { name: row.name }), allowNoOwnRate: false })}
                  >
                    {tr('billing', 'change')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {doctorsOf && <ServiceDoctorsDialog service={doctorsOf} editable={editable} onClose={() => setDoctorsOf(null)} />}
    </>
  );
}

function ServiceDoctorsDialog({ service, editable, onClose }: { service: ServiceRow; editable: boolean; onClose: () => void }) {
  const screen = useShareScreen('services');
  const [explain, setExplain] = useState<DoctorRateRow | null>(null);
  const doctors = useQuery({
    queryKey: ['doctor-share-service-doctors', service.id],
    queryFn: () => get<{ data: DoctorRateRow[] }>(`/accounts/doctor-share/services/${service.id}/doctors${qs({ limit: 100 })}`),
  });
  return (
    <Dialog open onClose={onClose} title={tr('billing', 'doctors_x', { name: service.name })} description={`Default share ${shareRateText(service, 'not set')}`} wide>
      <DataTable
        rows={doctors.data?.data}
        loading={doctors.isLoading} refreshing={doctors.isFetching}
        error={doctors.error}
        rowKey={(row) => row.doctorId}
        dense
        empty={tr('billing', 'no_doctors')}
        columns={[
          { header: tr('billing', 'doctor'), cell: (row) => row.doctorName },
          { header: tr('billing', 'own_rate_for_this_service'), align: 'right', cell: (row) => shareText(row.service) },
          { header: tr('billing', 'rate_for_all_x', { category: humanize(service.category) }), align: 'right', cell: (row) => shareText(row.category) },
          { header: tr('billing', 'scheduled'), cell: (row) => scheduledText(row.service) ?? scheduledText(row.category) ?? '—' },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setExplain(row)}>
                  {tr('billing', 'why_this_share')}
                </Button>
                {editable && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => screen.change({ scope: 'doctor_service', doctorId: row.doctorId, chargeItemId: service.id, title: `${row.doctorName} — ${service.name}`, allowNoOwnRate: true })}
                    >
                      {tr('billing', 'this_service')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => screen.change({ scope: 'doctor_category', doctorId: row.doctorId, category: service.category, title: tr('billing', 'x_every_x_service', { doctorName: row.doctorName, category: humanize(service.category) }), allowNoOwnRate: true })}
                    >
                      {tr('billing', 'whole_category')}
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]}
      />
      {explain && <ExplainDialog doctorId={explain.doctorId} doctorName={explain.doctorName} chargeItemId={service.id} onClose={() => setExplain(null)} />}
    </Dialog>
  );
}

function ExplainDialog({ doctorId, doctorName, chargeItemId, onClose }: { doctorId: string; doctorName: string; chargeItemId: string; onClose: () => void }) {
  const [date, setDate] = useState(todayIso());
  const explained = useQuery({
    queryKey: ['doctor-share-explain', doctorId, chargeItemId, date],
    queryFn: () => get<{ sharePct: number; shareMinor: number; source: string; effectiveFrom: string | null; explanation: string }>(`/accounts/doctor-share/explain${qs({ doctorId, chargeItemId, date })}`),
  });
  return (
    <Dialog open onClose={onClose} title={tr('billing', 'why_this_share_x', { doctorName: doctorName })}>
      <div className="space-y-3">
        <Field label={tr('billing', 'on')}>
          <DatePicker value={date} onChange={(value) => setDate(value)} />
        </Field>
        {explained.data && (
          <Alert>
            {shareRateText(explained.data.shareMinor > 0 ? { sharePct: null, shareMinor: explained.data.shareMinor } : explained.data, '0%')} — {explained.data.explanation}
            {explained.data.effectiveFrom ? ` (since ${dateOnly(explained.data.effectiveFrom)})` : ''}.
          </Alert>
        )}
        {explained.error && <Alert variant="destructive">{describeError(explained.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function DoctorsTab() {
  const toast = useToast();
  const [doctorId, setDoctorId] = useState('');
  const doctors = useQuery({ queryKey: ['doctor-list'], queryFn: () => get<{ data: { id: string; name: string }[] }>(`/accounts/doctor-share/doctors${qs({ limit: 100 })}`) });
  const card = useQuery({
    queryKey: ['doctor-rate-card', doctorId],
    queryFn: () =>
      get<{ doctor: { name: string }; rows: (ShareChangeView & { chargeItem: { code: string; name: string } | null; state: string })[] }>(`/accounts/doctor-share/doctors/${doctorId}`),
    enabled: Boolean(doctorId),
  });
  return (
    <>
      <SearchFilterBar
        filters={
          <FilterField label={tr('billing', 'doctor')}>
            <Select className="w-72" value={doctorId} onChange={(event) => setDoctorId(event.target.value)}>
              <option value="">{tr('billing', 'choose_a_doctor')}</option>
              {(doctors.data?.data ?? []).map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        trailing={
          doctorId && (
            <>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                {tr('billing', 'print_rate_card')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadGet(`/accounts/doctor-share/doctors/${doctorId}/rate-card.csv`, 'rate-card.csv').catch((error) => toast.error(tr('billing', 'could_not_download'), describeError(error)))}
              >
                {tr('billing', 'download_csv')}
              </Button>
            </>
          )
        }
        onReset={doctorId ? () => setDoctorId('') : undefined}
      />
      {doctorId && (
        <DataTable
          rows={card.data?.rows}
          loading={card.isLoading} refreshing={card.isFetching}
          error={card.error}
          rowKey={(row) => row.id}
          empty={tr('billing', 'this_doctor_has_no_rates_of_their_own_the')}
          columns={[
            { header: tr('billing', 'applies_to'), cell: (row) => (row.scope === 'doctor_category' ? `Every ${humanize(row.category)} service` : row.chargeItem ? `${row.chargeItem.code} ${row.chargeItem.name}` : '—') },
            { header: tr('billing', 'share_2'), align: 'right', cell: (row) => shareRateText(row, 'no own rate') },
            { header: tr('billing', 'from'), sort: (row) => row.effectiveFrom, cell: (row) => dateOnly(row.effectiveFrom) },
            { header: tr('billing', 'state'), sort: (row) => row.state, cell: (row) => <StatusBadge status={row.state} /> },
            { header: tr('billing', 'reference'), sort: (row) => row.reference, cell: (row) => row.reference ?? '—' },
          ]}
        />
      )}
    </>
  );
}

function WardTab({ editable }: { editable: boolean }) {
  const screen = useShareScreen('ward');
  const rules = useQuery({ queryKey: ['doctor-share-ward-rules'], queryFn: () => get<WardRuleRow[]>('/accounts/doctor-share/ward-rules') });
  return (
    <>
      <DataTable
        rows={rules.data}
        loading={rules.isLoading} refreshing={rules.isFetching}
        error={rules.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_ward_charge_is_credited_to_the_admitting')}
        columns={[
          { header: tr('billing', 'ward_charge'), sort: (row) => row.name, cell: (row) => row.name },
          { header: tr('billing', 'service'), sort: (row) => row.chargeItem.code, cell: (row) => `${row.chargeItem.code} ${row.chargeItem.name}` },
          { header: tr('billing', 'share_2'), align: 'right', cell: (row) => shareRateText(row.current, "doctor's own rate") },
          { header: tr('billing', 'scheduled'), cell: (row) => (row.scheduled[0] ? `${shareRateText(row.scheduled[0], "doctor's own rate")} from ${dateOnly(row.scheduled[0].effectiveFrom)}` : '—') },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              editable ? (
                <Button size="sm" variant="outline" onClick={() => screen.change({ scope: 'ward_rule', wardChargeRuleId: row.id, title: tr('billing', 'share_x', { name: row.name }), allowNoOwnRate: true })}>
                  {tr('billing', 'change')}
                </Button>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function ShareWaitingTab({ manage }: { manage: boolean }) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const waiting = useQuery({
    queryKey: ['doctor-share-changes', 'pending'],
    queryFn: () => get<Page<ShareChangeView>>(`/accounts/doctor-share/changes${qs({ status: 'pending', limit: 100 })}`),
  });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' | 'cancel' }) => post(`/accounts/doctor-share/changes/${id}/${action}`, {}),
    onSuccess: () => {
      toast.success(tr('billing', 'done'));
      void queryClient.invalidateQueries({ queryKey: ['doctor-share-changes'] });
      void queryClient.invalidateQueries({ queryKey: ['doctor-share-overview'] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_do_that'), describeError(error)),
  });
  return (
    <DataTable
      rows={waiting.data?.data}
      loading={waiting.isLoading} refreshing={waiting.isFetching}
      error={waiting.error}
      rowKey={(row) => row.id}
      empty={tr('billing', 'nothing_is_waiting')}
      columns={[
        { header: tr('billing', 'asked'), sort: (row) => row.requestedAt, cell: (row) => dateTime(row.requestedAt) },
        { header: tr('billing', 'share_2'), cell: (row) => `${humanize(row.scope)}: ${row.chargeItemName ?? row.category ?? row.wardRuleName ?? ''}${row.doctorName ? ` — ${row.doctorName}` : ''}` },
        { header: tr('billing', 'new_share'), align: 'right', cell: (row) => shareRateText(row, 'no own rate') },
        { header: tr('billing', 'starts_on'), sort: (row) => row.effectiveFrom, cell: (row) => dateOnly(row.effectiveFrom) },
        { header: tr('billing', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason },
        { header: tr('billing', 'asked_by'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
        {
          header: '',
          align: 'right',
          cell: (row) => (
            <div className="flex justify-end gap-2">
              {manage && row.requestedBy !== user?.id && (
                <>
                  <Button size="sm" onClick={() => act.mutate({ id: row.id, action: 'approve' })}>
                    {tr('billing', 'approve')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => act.mutate({ id: row.id, action: 'reject' })}>
                    {tr('billing', 'reject')}
                  </Button>
                </>
              )}
              {row.requestedBy === user?.id && (
                <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: row.id, action: 'cancel' })}>
                  {tr('billing', 'withdraw')}
                </Button>
              )}
            </div>
          ),
        },
      ]}
    />
  );
}

function ShareHistoryTab() {
  const toast = useToast();
  const [cursor, setCursor] = useState<string | undefined>();
  const history = useQuery({
    queryKey: ['doctor-share-changes', 'history', cursor],
    queryFn: () => get<Page<ShareChangeView>>(`/accounts/doctor-share/changes${qs({ cursor, limit: 50 })}`),
  });
  return (
    <>
      <SearchFilterBar
        trailing={
          <Button
            size="sm"
            variant="outline"
            onClick={() => downloadGet('/accounts/doctor-share/changes.csv', 'doctor-share-changes.csv').catch((error) => toast.error(tr('billing', 'could_not_download'), describeError(error)))}
          >
            {tr('billing', 'download_csv')}
          </Button>
        }
      />
      <DataTable
        rows={history.data?.data}
        loading={history.isLoading} refreshing={history.isFetching}
        error={history.error}
        rowKey={(row) => row.id}
        empty={tr('billing', 'no_changes_yet')}
        footer={
          cursor || history.data?.nextCursor ? (
            <div className="ms-auto flex gap-2">
              {cursor && <Button variant="outline" size="sm" onClick={() => setCursor(undefined)}>{tr('billing', 'first_page')}</Button>}
              {history.data?.nextCursor && <Button variant="outline" size="sm" onClick={() => setCursor(history.data?.nextCursor ?? undefined)}>{tr('billing', 'next_page')}</Button>}
            </div>
          ) : undefined
        }
        columns={[
          { header: tr('billing', 'when'), sort: (row) => row.requestedAt, cell: (row) => dateTime(row.requestedAt) },
          { header: tr('billing', 'share_2'), cell: (row) => `${humanize(row.scope)}: ${row.chargeItemName ?? row.category ?? row.wardRuleName ?? ''}${row.doctorName ? ` — ${row.doctorName}` : ''}` },
          { header: tr('billing', 'value'), align: 'right', cell: (row) => shareRateText(row, 'no own rate') },
          { header: tr('billing', 'from'), sort: (row) => row.effectiveFrom, cell: (row) => dateOnly(row.effectiveFrom) },
          { header: tr('billing', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
          { header: tr('billing', 'reason'), sort: (row) => row.reason, cell: (row) => row.reason },
          { header: tr('billing', 'reference'), sort: (row) => row.reference, cell: (row) => row.reference ?? '—' },
          { header: tr('billing', 'by_2'), sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
        ]}
      />
    </>
  );
}
